import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Navbar() {
  const { t } = useTranslation('nav');
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const isActive = (path: string) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link
            to="/"
            className="flex items-center gap-2.5 group whitespace-nowrap"
          >
            <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center bg-primary-500 rounded-lg transition-transform duration-300 group-hover:scale-110">
              <i className="ri-fire-fill text-background-50 text-lg md:text-xl"></i>
            </div>
            <span
              className={`font-heading font-bold text-lg md:text-xl transition-colors duration-300 ${
                scrolled ? 'text-foreground-900' : 'text-background-50'
              }`}
            >
              开云体育
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className={`relative font-medium text-sm transition-colors duration-300 whitespace-nowrap py-1 ${
                isActive('/')
                  ? scrolled
                    ? 'text-primary-600'
                    : 'text-background-50'
                  : scrolled
                    ? 'text-foreground-700 hover:text-primary-500'
                    : 'text-background-50/80 hover:text-background-50'
              }`}
            >
              {t('nav_home')}
              {isActive('/') && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500 rounded-full" />
              )}
            </Link>
            <Link
              to="/category/football"
              className={`relative font-medium text-sm transition-colors duration-300 whitespace-nowrap py-1 ${
                isActive('/category')
                  ? scrolled
                    ? 'text-primary-600'
                    : 'text-background-50'
                  : scrolled
                    ? 'text-foreground-700 hover:text-primary-500'
                    : 'text-background-50/80 hover:text-background-50'
              }`}
            >
              {t('nav_categories')}
              {isActive('/category') && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500 rounded-full" />
              )}
            </Link>
            <Link
              to="/about"
              className={`relative font-medium text-sm transition-colors duration-300 whitespace-nowrap py-1 ${
                isActive('/about')
                  ? scrolled
                    ? 'text-primary-600'
                    : 'text-background-50'
                  : scrolled
                    ? 'text-foreground-700 hover:text-primary-500'
                    : 'text-background-50/80 hover:text-background-50'
              }`}
            >
              {t('nav_about')}
              {isActive('/about') && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500 rounded-full" />
              )}
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <button className="px-5 py-2 text-sm font-semibold rounded-full border-2 transition-all duration-300 whitespace-nowrap cursor-pointer hover:scale-105 border-background-50/60 text-background-50 hover:bg-background-50 hover:text-foreground-900">
              {t('nav_login')}
            </button>
            <button className="px-5 py-2 text-sm font-semibold rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer hover:scale-105 bg-primary-500 text-background-50 hover:bg-primary-600">
              {t('nav_register')}
            </button>
          </div>

          <button
            className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menu"
          >
            <i
              className={`text-xl transition-colors duration-300 ${
                scrolled ? 'text-foreground-900' : 'text-background-50'
              } ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}
            ></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 shadow-lg animate-fade-in">
          <div className="px-4 py-4 flex flex-col gap-3">
            <Link
              to="/"
              className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                isActive('/')
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              {t('nav_home')}
            </Link>
            <Link
              to="/category/football"
              className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                isActive('/category')
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              {t('nav_categories')}
            </Link>
            <Link
              to="/about"
              className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                isActive('/about')
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              {t('nav_about')}
            </Link>
            <div className="flex gap-3 pt-2 border-t border-background-200">
              <button className="flex-1 px-4 py-2.5 text-sm font-semibold rounded-full border-2 border-primary-300 text-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer hover:bg-primary-50">
                {t('nav_login')}
              </button>
              <button className="flex-1 px-4 py-2.5 text-sm font-semibold rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer bg-primary-500 text-background-50 hover:bg-primary-600">
                {t('nav_register')}
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}